import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import defaultTheme from "@/config/muiTheme";
import React from "react";
import PropTypes from "prop-types";
import { logger } from "../helpers/loggerHelper";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Collapse,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

type ErrorBoundaryReport = {
  errorId: string;
  error: Error;
  errorInfo: React.ErrorInfo;
};

type ErrorBoundaryProps = {
  children?: React.ReactNode;
  onReport?: (_report: ErrorBoundaryReport) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
  errorId: string | null;
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    logger.error("ErrorBoundary caught an error", { error });
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorId = `ERR-${Date.now()}`;
    this.setState({ error, errorInfo, errorId });
    // Log to monitoring service or logger
    try {
      logger.error("ErrorBoundary caught an error", {
        errorId,
        error,
        errorInfo,
      });
    } catch (e) {
      // fallback to console
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary log failed", e);
    }
    if (this.props.onReport) {
      try {
        this.props.onReport({ errorId, error, errorInfo });
      } catch (e) {
        // ignore
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  copyDetails = async () => {
    const payload = {
      id: this.state.errorId,
      message: this.state.error?.toString(),
      stack:
        this.state.errorInfo?.componentStack || this.state.error?.stack || "",
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch (e) {
      // ignore
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { errorId, error, errorInfo, showDetails } = this.state;

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={defaultTheme}>
          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.default",
              p: 2,
            }}
            role="alert"
            aria-live="assertive"
          >
            <Paper elevation={3} sx={{ maxWidth: 920, width: "100%", p: 3 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <BugReportIcon color="error" fontSize="large" />
                <Box>
                  <Typography variant="h6">Something went wrong</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    An unexpected error occurred. Try reloading the page or
                    contact support if the problem persists.
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Error ID: {errorId}
                  </Typography>
                </Box>
                <Box sx={{ marginLeft: "auto" }}>
                  <IconButton
                    aria-label="copy error details"
                    onClick={this.copyDetails}
                    size="large"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RestartAltIcon />}
                  onClick={this.handleReload}
                >
                  Reload
                </Button>

                {/* <Button
              variant="outlined"
              startIcon={<BugReportIcon />}
              onClick={() => {
                const subject = encodeURIComponent(
                  `App error report - ${errorId}`
                );
                const body = encodeURIComponent(
                  `Error ID: ${errorId}\n\nMessage: ${error?.toString()}\n\nStack:\n${errorInfo?.componentStack || ""}`
                );
                window.location.href = `mailto:support@example.com?subject=${subject}&body=${body}`;
              }}
            >
              Report
            </Button> */}

                <Button
                  onClick={() =>
                    this.setState((s) => ({ showDetails: !s.showDetails }))
                  }
                >
                  {showDetails ? "Hide details" : "Show details"}
                </Button>
              </Box>

              <Collapse in={showDetails}>
                <Box
                  component="pre"
                  sx={{
                    mt: 2,
                    whiteSpace: "pre-wrap",
                    fontSize: 12,
                    bgcolor: "background.paper",
                    p: 2,
                    borderRadius: "var(--radius-container)",
                  }}
                >
                  {error?.toString()}
                  {"\n"}
                  {errorInfo?.componentStack}
                </Box>
              </Collapse>
            </Paper>
          </Box>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  onReport: PropTypes.func,
};

export default ErrorBoundary;
